import { type AdSet, EMPTY_EXCLUDED_GEO, type LegacyPublishDraft, type MetaAdDraft } from '../types/metaAdDraft.js';

// Multi-ad-set Phase 1 — the publish path's BEHAVIOR-PRESERVING adapter. The
// publish mapper/service were written against the old single-ad-set
// {campaign, adSet, ad} triple. Rather than rework that (publish-orchestration
// changes are Phase 2 / Part 2), this pure projection reconstructs that exact
// triple from the new multi-ad-set draft: the FIRST ad set + the campaign-wide
// locked fields + the shared creative. For a 1-ad-set campaign this is the
// identical composition the mapper saw before — so publish stays byte-identical.
//
// Part 2 replaces the single-ad-set projection with the per-ad-set fan-out (one
// create per ad set under the shared campaign + creative). Pure: no I/O.

// A defensive empty ad set — never expected (a draft always has >= 1), but keeps
// the projection total so a malformed doc throws downstream (no body) rather than
// here. Reuses the migration's empty-targeting expectations via the field shape.
const EMPTY_AD_SET: AdSet = {
  id: 'adset-1',
  name: '',
  targeting: {
    geoLevel: 'country',
    countries: [],
    cities: [],
    regions: [],
    places: [],
    neighborhoods: [],
    zips: [],
    customLocations: [],
    excluded: EMPTY_EXCLUDED_GEO,
    locationTypes: 'home_recent',
    ageMin: 18,
    ageMax: 65,
    gender: 'all',
    interests: [],
    sourceType: 'manual',
    sourcePresetId: null,
    sourceCampaignId: null,
    sourceCampaignName: null,
    sourceAdSetId: null,
    sourceAdSetName: null,
    rawPayload: null,
  },
  budget: null,
  datesOverridden: false,
  startDate: null,
  endDate: null,
  status: 'ACTIVE',
  publishState: null,
};

// Resolve an ad set's effective schedule: its own dates when overridden, else the
// campaign default.
export function resolveAdSetDates(
  adSet: Pick<AdSet, 'datesOverridden' | 'startDate' | 'endDate'>,
  campaign: { defaultStartDate: string | null; defaultEndDate: string | null },
): { startDate: string | null; endDate: string | null } {
  if (adSet.datesOverridden) return { startDate: adSet.startDate, endDate: adSet.endDate };
  return { startDate: campaign.defaultStartDate, endDate: campaign.defaultEndDate };
}

// Project a multi-ad-set draft → the legacy publish-view triple for `adSets[index]`
// (default 0, the only one a Part-1 publish targets).
export function firstAdSetLegacyView(draft: MetaAdDraft, index = 0): LegacyPublishDraft {
  const c = draft.campaign;
  const adSet = draft.adSets[index] ?? draft.adSets[0] ?? EMPTY_AD_SET;
  const dates = resolveAdSetDates(adSet, c);
  return {
    id: draft.id,
    title: draft.title,
    campaign: {
      objective: c.objective,
      budgetLevel: c.budgetMode === 'CBO' ? 'campaign' : 'adset',
      budget: c.budget,
      publishStatus: c.publishStatus,
    },
    adSet: {
      name: adSet.name,
      optimizationGoal: c.optimizationGoal,
      customEventType: c.customEventType,
      destinationType: c.destinationType,
      facebookPageId: c.facebookPageId,
      facebookPageName: c.facebookPageName,
      startDate: dates.startDate,
      endDate: dates.endDate,
      targeting: adSet.targeting,
      placements: c.placements,
      budget: adSet.budget,
      status: adSet.status,
    },
    ad: draft.ads,
  };
}
